import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { CloseIcon, EditableText, IconButton, SkeletonLoader, Tooltip } from "@activecollab/components";
import PropTypes from "prop-types";
import UserLink from "~/components/UserLink/UserLink";
import { UserWeeklyCapacity } from "~/components/Users/UserWeeklyCapacity";
import { getUserWorkweekById } from "~/store/selectors/selector.weeklyWorkerCapacity";

export const TeamMemberItem = ({ user, isLoading, onClick, onChange, showCapacity }) => {
  const { t } = useTranslation();
  const workWeekData = useSelector((state) => getUserWorkweekById(state, user?.id));

  const weeklyCapacity = useMemo(() => {
    if (user?.id) return workWeekData;
    const sum = user?.weekly_worker_capacity.reduce((accumulator, currentValue) => accumulator + currentValue, 0);
    return { weekly_capacity: sum };
  }, [user?.id, user?.weekly_worker_capacity, workWeekData]);

  const handleClick = () => {
    if (onClick) {
      onClick(user?.uuid);
    }
  };

  const handleChangeFirstName = (e) => {
    onChange({ ...user, first_name: e.target.value });
  };

  const handleChangeLastName = (e) => {
    onChange({ ...user, last_name: e.target.value });
  };

  const editable = typeof onClick === "function" && !!user?.uuid;

  return (
    <div className="tw-flex tw-items-center tw-justify-between tw-h-8 tw-mb-2">
      <div className="tw-flex tw-items-center">
        {editable ? (
          <div className="tw-flex tw-items-center">
            <UserLink
              id={0}
              name={`${user?.first_name} ${user?.last_name}`}
              notInteractive
              className="tw-mr-1"
              hideName
              context="TeamMemberItem"
              email={`${user?.first_name}.${user?.last_name}@example.com`}
            />
            <EditableText
              data-testid="editable-first-name"
              value={user?.first_name}
              onChange={handleChangeFirstName}
            />
            <EditableText
              data-testid="editable-last-name"
              value={user?.last_name}
              onChange={handleChangeLastName}
            />
          </div>
        ) : (
          <div data-testid="user-link">
            <UserLink
              id={user?.id}
              name={user?.name}
              className="tw-w-[200px] tw-truncate"
              notInteractive
              context="TeamMemberItem"
            />
          </div>
        )}
      </div>
      <div className="tw-flex tw-items-center tw-gap-2">
        {showCapacity ? (
          !isLoading ? (
            <UserWeeklyCapacity workWeekData={weeklyCapacity} />
          ) : (
            <SkeletonLoader
              data-testid="skeleton-loader"
              className="tw-h-5 tw-w-10"
            />
          )
        ) : null}
        {editable ? (
          <Tooltip title={t("Remove Member")}>
            <IconButton
              onClick={handleClick}
              variant="text gray"
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Tooltip>
        ) : (
          <div className="tw-w-6 tw-shrink-0" />
        )}
      </div>
    </div>
  );
};

TeamMemberItem.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    uuid: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    weekly_worker_capacity: PropTypes.array,
  }).isRequired,
  isLoading: PropTypes.bool,
  onClick: PropTypes.func,
  onChange: PropTypes.func,
  showCapacity: PropTypes.bool,
};
